'use client';

/**
 * HeaderClient.tsx
 *
 * Interactive client component for the marketing header.
 * Receives isLogin / userRole from the server wrapper (Header.tsx).
 *
 * Manages:
 *   - Desktop mega-panel open/close (hover + focus, with debounced close)
 *   - Mobile drawer open/close (focus restored to the hamburger on close)
 *   - Active-link detection from usePathname
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { BASE_IMAGE_URL } from '@/constants/path';
import { UserRole } from '@/lib/types/account';
import { useLocaleSwitch } from '@/hooks/use-locale-switch';
import Icon from '@/components/marketing/primitives/Icon';
import Btn from '@/components/marketing/primitives/Btn';
import { ICON_MAP } from '@/components/marketing/theme';
import { SERVICES } from '@/components/marketing/data/services';
import { toMainSiteHref } from '@/lib/utils/main-site-href';
import { NAV_CONFIG, NavItem, NavItemMegaCompact, NavMegaSide } from './nav-config';
import BrandStrip, { HEADER_CONTAINER, HEADER_CONTAINER_WIDE } from './BrandStrip';
import MegaPanel from './MegaPanel';
import MobileDrawer from './MobileDrawer';
import InquiryModal from '@/components/marketing/inquiry/InquiryModal';
import { INQUIRY_ANCHOR, hasInquirySection, isPlainLeftClick } from '@/components/marketing/inquiry/anchor';
import HeaderUserMenu from '@/components/shared/molecules/desktop/HeaderUserMenu';

interface HeaderClientProps {
  isLogin: boolean;
  /**
   * 상담 CTA 의 `href`. 기본값은 같은 문서의 하단 상담 섹션(`#inquiry`) 이고, 그 섹션을
   * 끄는 레이아웃만 폼이 있는 주소로 갈아 끼운다(`MarketingLayout`).
   *
   * JS 가 붙은 뒤에는 클릭을 가로채 모달을 열므로 이 값은 **하이드레이션 전이나 JS 가
   * 실패했을 때**만 쓰인다. 그래도 죽은 앵커를 남겨 둘 이유는 없다.
   */
  inquiryHref?: string;
  /**
   * 로고가 가리킬 홈 주소. 보통 '/' 지만 blog 서브도메인에서는 메인 호스트 절대 URL 이다 —
   * 거기서 '/' 는 미들웨어가 /media 로 되돌려보내 미디어센터를 벗어날 수 없다.
   */
  homeHref?: string;
  /**
   * blog 서브도메인에서 렌더될 때의 메인 호스트 origin(예: https://lawkit.kr). 메인 호스트에서는 ''.
   * 메인 앱 링크를 이 origin 의 절대 URL 로 건다 — 상대 경로면 미들웨어가 링크마다 302로 되돌려 보낸다.
   */
  mainOrigin?: string;
  userRole?: UserRole;
  /**
   * 로그인 이후 앱 화면(마이페이지·빠른 접수·로그인 등)에서 렌더될 때 켠다.
   * 마케팅 페이지에서는 끈다(기본값).
   *
   * 두 가지가 달라진다:
   *   1) 언어 전환 — 앱은 URL 이 하나뿐이라 쿠키+리로드로 같은 화면을 다시 받는다.
   *      마케팅은 '/en' 이라는 별도 트리로 이동한다. use-locale-switch.ts 참고.
   *   2) CTA — '#inquiry' 는 마케팅 페이지에만 있는 앵커라 앱 화면에서는 눌러도 아무 일이 없다.
   */
  appMode?: boolean;
  /**
   * 아직 번역되지 않은 화면(변호사 대시보드·국문 로그인·신청 위저드)에서 켠다.
   * 거기에 KO/EN 토글을 띄우면 눌러도 화면은 그대로인데 LK_LOCALE 쿠키만 180일 박히고,
   * 이후 '/' 접속이 /en 으로 리다이렉트된다(src/middleware.ts).
   */
  hideLocaleSwitch?: boolean;
  /**
   * 헤더 상자를 **본문과 같은 폭**으로 넓힌다. 마이페이지에서만 켠다.
   *
   * 마이페이지 본문은 스펙상 1856 까지 늘어나는데 헤더가 1280 에 머물면, 1920 화면에서 로고가
   * 사이드바보다 300px 넘게 오른쪽에 서서 셸이 두 조각으로 보인다. `appMode` 를 쓰지 않는 이유는
   * 로그인·신청 위저드도 그 값을 켜는데 그쪽은 1280 이 맞기 때문이다.
   */
  wide?: boolean;
}

type BoundaryLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

/** /media는 blog 호스트 경계를 넘는다. Next Link의 RSC prefetch 대신 문서 이동을 사용한다. */
function BoundaryLink({ href, children, ...props }: BoundaryLinkProps) {
  return href === '/media' || href.startsWith('/media/') ? (
    <a href={href} {...props}>
      {children}
    </a>
  ) : (
    <Link href={href} {...props}>
      {children}
    </Link>
  );
}

const INQUIRY_HEADING = '상담 신청';
/** 모달 제목 아래 한 줄. 하단 폼이 없는 화면에서만 뜨므로 그 사정을 먼저 말한다. */
const INQUIRY_NOTICE = '내용을 남겨 주시면 담당 변호사가 확인 후 회신드립니다.';

/** 캐럿 버튼의 aria-controls 와 패널의 id 를 한 곳에서 맞춘다. */
const megaPanelId = (key: string) => `mega-panel-${key}`;

/** Side blurb + CTA shared by both mega panels — single source of truth is NAV_CONFIG. */
function MegaSide({
  side,
  onCtaClick,
  onInquiryFallback,
  inquiryHref,
  mainOrigin,
}: {
  side: NavMegaSide;
  onCtaClick: () => void;
  onInquiryFallback: () => void;
  inquiryHref: string;
  mainOrigin: string;
}) {
  const label = (
    <>
      {side.cta.label}
      <Icon name={ICON_MAP.arrowRight} className="text-[14px]" />
    </>
  );

  /**
   * 상담 CTA 만 특별하다 — 하단 폼이 있으면 앵커 그대로, 없으면 모달이다(`inquiry/anchor`).
   *
   * 모달로 가는 경우에는 **패널을 닫지 않는다.** 닫으면 방금 누른 버튼이 사라져, 모달이
   * 포커스를 되돌릴 자리가 없어진다(`useModalA11y` 는 열 때의 activeElement 로 돌아간다).
   * 모달이 패널 위를 덮으므로 열려 있어도 보이지 않는다.
   */
  const onInquiryClick = (e: React.MouseEvent<HTMLElement>) => {
    // 새 탭으로 열려는 클릭은 링크 그대로 둔다 — 패널도 닫지 않는다(그대로 남아 있어야 자연스럽다).
    if (!isPlainLeftClick(e)) return;
    if (hasInquirySection()) {
      onCtaClick();
      return;
    }
    e.preventDefault();
    onInquiryFallback();
  };

  if (side.cta.href === INQUIRY_ANCHOR) {
    return (
      <>
        <h4 className="text-[12px] font-bold tracking-[0.08em] uppercase text-lawkitBlue-70 mb-3">{side.title}</h4>
        <p className="text-label1Regular text-lawkitGray-600 leading-[1.55] mb-[18px]">{side.blurb}</p>
        <Btn href={inquiryHref} variant="ghost" size="sm" onClick={onInquiryClick}>
          {label}
        </Btn>
      </>
    );
  }

  const ctaProps = side.cta.to ? { to: toMainSiteHref(mainOrigin, side.cta.to) } : { href: side.cta.href as string };
  return (
    <>
      <h4 className="text-[12px] font-bold tracking-[0.08em] uppercase text-lawkitBlue-70 mb-3">{side.title}</h4>
      <p className="text-label1Regular text-lawkitGray-600 leading-[1.55] mb-[18px]">{side.blurb}</p>
      <Btn {...ctaProps} variant="ghost" size="sm" onClick={onCtaClick}>
        {label}
      </Btn>
    </>
  );
}

export default function HeaderClient({ isLogin, userRole, appMode, hideLocaleSwitch, wide, homeHref = '/', mainOrigin = '', inquiryHref = INQUIRY_ANCHOR }: HeaderClientProps) {
  // 로고·메뉴·메가 패널이 같은 상자를 쓴다 — 하나만 넓히면 드롭다운이 자기 트리거와 어긋난다.
  const container = wide ? HEADER_CONTAINER_WIDE : HEADER_CONTAINER;
  const siteHref = (href: string) => toMainSiteHref(mainOrigin, href);
  const pathname = usePathname();
  const [open, setOpen] = useState<string | null>(null); // mega panel key or null
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  // 브랜드 띠 때문에 스크롤 상단에서는 헤더가 y=0 이 아니다 — 드로어를 헤더 실제 하단에 맞춘다.
  const [drawerTop, setDrawerTop] = useState(84);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  const openMega = useCallback((key: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(key);
  }, []);

  /**
   * 모달이 열려 있는 동안은 패널을 닫지 않는다. **ref 인 이유는 타이머와 전역 리스너가
   * 읽기 때문이다** — 상태를 그대로 읽으면 등록 시점의 값에 얼어붙는다.
   *
   * **올리는 것은 즉시, 내리는 것은 렌더 뒤다.**
   *
   * 올릴 때: 모달이 포커스를 가져가는 순간 패널에 blur 가 나므로, 상태가 반영되기를
   * 기다리면 그 사이에 닫기가 예약된다. 그래서 `openInquiryModal` 이 직접 올린다.
   *
   * 내릴 때: Escape 는 모달(`document` 리스너)이 먼저 받고 헤더(`window` 리스너)가 나중에
   * 받는다. 모달 쪽 `onClose` 에서 ref 를 내리면, 곧바로 이어지는 헤더 리스너가 그걸 보고
   * 패널까지 닫는다 — 그러면 모달이 포커스를 되돌릴 CTA 가 같은 커밋에서 사라져 포커스가
   * `body` 에 떨어진다(실측). effect 로 미루면 그 한 번의 Escape 동안은 ref 가 올라간
   * 상태라 헤더가 건너뛰고, 패널은 다음 Escape 에 닫힌다.
   */
  const inquiryOpenRef = useRef(false);
  useEffect(() => {
    if (!inquiryOpen) inquiryOpenRef.current = false;
  }, [inquiryOpen]);

  const scheduleClose = useCallback(() => {
    // 모달이 포커스를 가져가면 패널에 blur 가 난다. 그때 패널을 닫으면 방금 누른 CTA 가
    // 사라져, 모달이 닫힐 때 포커스를 되돌릴 자리가 없어진다(useModalA11y 는 열 때의
    // activeElement 로 돌아간다). 모달이 패널을 덮으므로 열려 있어도 보이지 않는다.
    if (inquiryOpenRef.current) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(null), 140);
  }, []);

  /** 포커스가 헤더 안에 머무는 동안 예약된 닫기를 취소한다. EnHeaderClient 와 같은 방식. */
  const cancelScheduledClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  // Clear any pending close timer on unmount
  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  // Close everything on route change
  useEffect(() => {
    setOpen(null);
    setDrawerOpen(false);
  }, [pathname]);

  // Keyboard: Escape closes open panels / drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // 모달이 떠 있으면 Escape 는 모달 몫이다. 여기서 같이 받으면 패널이 함께 닫히면서
      // 포커스를 되돌릴 CTA 가 사라진다 — 모달을 닫은 뒤 한 번 더 누르면 패널도 닫힌다.
      if (inquiryOpenRef.current) return;
      if (e.key === 'Escape') {
        setOpen(null);
        setDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 뷰포트가 lg 경계를 넘을 때, 반대편 전용 오버레이가 남지 않게 정리한다.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => {
      // → 데스크탑: lg:hidden 드로어가 사라지며 스크롤 잠금만 남는 걸 막는다.
      // → 모바일: 메가 패널은 hidden lg:flex 인 nav 로만 열 수 있는데,
      //   열린 채로 폭이 줄면 닫을 방법 없이 히어로를 덮는다.
      if (e.matches) setDrawerOpen(false);
      else setOpen(null);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 드로어가 열려 있는 동안 헤더의 실제 하단에 맞춘다.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const update = () => setDrawerTop(headerRef.current?.getBoundingClientRect().bottom ?? 84);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [drawerOpen]);

  const switchToEnglish = useLocaleSwitch('en', appMode);

  /**
   * 상단 CTA.
   *   로그인 O → 빠른 접수(/quick-intake). 이미 고객인 사람에게는 상담 폼보다 접수가 맞는 동선이다.
   *   로그인 X + 마케팅 → 상담 폼(#inquiry). 그 섹션이 없는 화면에서만 모달로 갈린다.
   *   로그인 X + 앱 → 없음. 종전대로.
   */
  const showInquiryCta = !isLogin && !appMode;

  /**
   * 모달을 연다. **ref 를 먼저 세우고 예약된 닫기를 취소한다** — 순서가 바뀌면 이미 걸려
   * 있던 140ms 타이머가 그대로 터져 패널이 닫힌다.
   */
  const openInquiryModal = () => {
    inquiryOpenRef.current = true;
    cancelScheduledClose();
    setInquiryOpen(true);
  };

  /**
   * 모달이 닫힌 뒤 포커스가 갈 데를 잃었으면 햄버거로 넘긴다.
   *
   * 보통은 `useModalA11y` 가 열 때의 컨트롤로 되돌린다. 그런데 그 컨트롤이 모달이 떠 있는
   * 동안 사라지는 길이 하나 남아 있다 — 창을 lg(1024px) 아래로 줄이면 위 matchMedia 효과가
   * 메가패널을 닫는다(닫지 않으면 좁은 화면에서 패널을 닫을 방법이 없다). 그러면 되돌릴
   * 자리가 없어 포커스가 `body` 에 떨어지고, 다음 Tab 이 문서 맨 앞으로 간다.
   *
   * 훅의 복귀는 언마운트 정리에서 일어나므로 그 뒤에 본다. 여전히 `body` 일 때만 손대고,
   * 햄버거가 그 폭에서 실제로 보일 때만 옮긴다(`offsetParent`).
   */
  const rescueFocusAfterClose = () => {
    requestAnimationFrame(() => {
      if (document.activeElement !== document.body) return;
      const hamburger = hamburgerRef.current;
      if (hamburger?.offsetParent) hamburger.focus();
    });
  };

  /**
   * 상담 CTA 클릭. **하단 폼이 있으면 앵커 기본 동작 그대로 둔다.**
   *
   * 모달을 항상 열면 폼이 한 화면에 두 벌이 된다. 둘은 같은 Zustand 스토어와 같은 `iq-*`
   * id 를 쓰므로, 나중에 마운트된 쪽이 `clearForm()` 으로 앞의 입력을 지우고 라벨이 어느
   * 입력을 가리키는지도 깨진다. 서비스 상세가 넘기는 `serviceContext`(문의 유형 선선택)도
   * 하단 폼만 갖고 있다. 모달은 **앵커가 죽어 있던 화면**을 위한 것이지 대체재가 아니다.
   */
  const onInquiryClick = (e: React.MouseEvent<HTMLElement>) => {
    // 새 탭으로 열려는 클릭은 링크 그대로 둔다 — isPlainLeftClick 주석 참고.
    if (!isPlainLeftClick(e)) return;
    if (hasInquirySection()) return;
    e.preventDefault();
    openInquiryModal();
  };

  // Determine if a nav item is active — exact match or a nested child route.
  const matchRoute = (route: string): boolean => pathname === route || pathname.startsWith(route + '/');
  const isActive = (item: NavItem): boolean => {
    // 허브 라우트 하나로 하위 경로까지 덮인다(/services → /services/*, /media → /media/*).
    if (matchRoute(item.to)) return true;
    // 고객 지원만 예외 — 대표 링크(/faq) 외 나머지 지원 페이지도 활성으로 본다.
    if (item.key === 'support') return ['/introduce', '/company', '/partners'].some(matchRoute);
    return false;
  };

  const servicesItem = NAV_CONFIG.find(n => n.key === 'services');
  const servicesSide = servicesItem?.type === 'mega' ? servicesItem.side : null;

  /** All mega-compact nav items — panels are rendered generically from this list. */
  const megaCompactItems = NAV_CONFIG.filter((n): n is NavItemMegaCompact => n.type === 'mega-compact');

  return (
    <>
      {/* 브랜드 띠 — 예전에는 로고 아래 붙어 있던 문구다. BrandStrip.tsx 참고. */}
      <BrandStrip container={container}>
        <span className="inline-flex items-center gap-2 text-[11.5px] font-medium text-lawkitBlue-70">
          <span className="w-[5px] h-[5px] rounded-full bg-lawkitBlue-70 shadow-[0_0_0_3px_rgba(52,110,218,0.16)] shrink-0" />
          법무법인 오킴스 기업자문팀 공식 서비스
        </span>
      </BrandStrip>

      {/* 포커스 감지는 헤더 전체에 건다 — 메가 패널은 우측 액션보다 뒤에 렌더되므로,
          각 메뉴 래퍼에서 blur 를 잡으면 캐럿에서 패널로 Tab 하는 도중에 닫혀버린다. */}
      <header
        ref={headerRef}
        className="sticky top-0 z-[60] bg-white/[0.92] backdrop-saturate-[180%] backdrop-blur-md border-b border-lawkitGray-200"
        onMouseLeave={scheduleClose}
        onFocusCapture={cancelScheduledClose}
        onBlurCapture={scheduleClose}
      >
        <div className={`${container} h-[64px] flex items-center justify-between gap-6`}>
          {/* Brand — 로고만. 문구는 위 BrandStrip 으로 올라갔다. */}
          <Link href={homeHref} className="flex items-center gap-2 shrink-0">
            <Image src={`${BASE_IMAGE_URL}/logos/logo.png?v=1.0.1`} alt="LawKit" width={100} height={24} priority />
          </Link>

          {/* Desktop nav — hidden below lg */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="주 메뉴">
            {NAV_CONFIG.map(item => {
              const active = isActive(item);
              // Base Tailwind classes shared by nav buttons and links
              const navCls =
                'inline-flex items-center gap-1 px-3.5 py-2 text-body3Medium ' + 'text-lawkitGray-700 rounded-lg transition-colors duration-150 ' + 'hover:text-lawkitGray-950 hover:bg-lawkitGray-100';
              const activeCls = active ? ' text-lawkitGray-950 bg-lawkitGray-100' : '';
              if (item.type === 'mega' || item.type === 'mega-compact') {
                const expanded = open === item.key;
                const megaLabelCls =
                  'inline-flex items-center py-2 pl-3.5 pr-1 text-body3Medium rounded-l-lg' +
                  ' transition-colors duration-150 group-hover:text-lawkitGray-950' +
                  (active ? ' text-lawkitGray-950' : ' text-lawkitGray-700');
                /* 라벨과 캐럿을 나눈다 — 라벨은 허브로 이동하는 링크, 캐럿은 패널 토글 버튼.
                   하나의 엘리먼트로는 '이동'과 '열기' 중 하나만 활성화 동작으로 노출할 수 있고,
                   그러면 키보드·터치 사용자가 나머지 하나에 도달할 수 없다.
                   호버 배경은 두 조각을 감싸는 래퍼에 걸어 기존 pill 모양을 유지한다. */
                return (
                  <div
                    key={item.key}
                    className={`group flex items-center rounded-lg transition-colors duration-150 hover:bg-lawkitGray-100${active ? ' bg-lawkitGray-100' : ''}`}
                    onMouseEnter={() => openMega(item.key)}
                  >
                    {/* aria-current 는 정확히 그 URL 일 때만 — /services/kr-contract 에서
                        /services 링크가 '현재 페이지'로 읽히면 안 된다. 시각적 활성 표시는
                        하위 경로까지 유지한다(active). */}
                    <BoundaryLink href={siteHref(item.to)} aria-current={pathname === item.to ? 'page' : undefined} className={megaLabelCls}>
                      {item.label}
                    </BoundaryLink>
                    <button
                      type="button"
                      className="inline-flex items-center py-2 pl-0.5 pr-3 rounded-r-lg"
                      /* aria-haspopup="menu" 는 쓰지 않는다 — 열리는 것은 role="region" 인
                         링크 목록이라, menu 를 선언하면 화살표 키 탐색을 기대하게 만든다. */
                      aria-expanded={expanded}
                      aria-controls={megaPanelId(item.key)}
                      aria-label={`${item.label} 하위 메뉴 ${expanded ? '닫기' : '열기'}`}
                      onClick={() => setOpen(cur => (cur === item.key ? null : item.key))}
                    >
                      <Icon name={ICON_MAP.caretDown} className={`text-[12px] text-lawkitGray-400 transition-transform duration-200 ${expanded ? 'rotate-180 !text-lawkitGray-950' : ''}`} />
                    </button>
                  </div>
                );
              }
              return (
                <Link key={item.key} href={siteHref(item.to)} aria-current={active ? 'page' : undefined} className={`${navCls}${activeCls}`}>
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop right actions — hidden below lg */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            {/* 언어 전환 — 마케팅은 <a>로 /en 트리로 전체 로드, 앱은 쿠키+리로드(URL 하나) */}
            {!hideLocaleSwitch && (
              <div className="flex items-center gap-1.5 pr-2.5 border-r border-lawkitGray-200 text-[13px] font-semibold">
                <Icon name="ri-global-line" className="text-[15px] text-lawkitGray-400" />
                <span className="text-lawkitGray-950">KO</span>
                <span className="text-lawkitGray-300" aria-hidden="true">
                  /
                </span>
                {appMode ? (
                  <button type="button" lang="en" onClick={switchToEnglish} className="text-lawkitGray-600 hover:text-lawkitGray-950 transition-colors" aria-label="View in English">
                    EN
                  </button>
                ) : (
                  <a href={siteHref('/en')} lang="en" hrefLang="en" onClick={switchToEnglish} className="text-lawkitGray-600 hover:text-lawkitGray-950 transition-colors" aria-label="View in English">
                    EN
                  </a>
                )}
              </div>
            )}
            {/* LKUR 이 없어도(만료·구버전 쿠키) 로그인 상태면 사용자 메뉴를 보여준다.
                예전처럼 로그인 버튼으로 되돌리면 /sign-in 이 다시 튕겨내 눌러도 아무 일이 없다.
                역할 미상은 'C' 로 두고, 실제 역할은 /my-contract/dashboard 가 서버에서 확정해
                변호사면 /lawyer 로 다시 보낸다. */}
            {isLogin ? (
              <HeaderUserMenu userRole={userRole ?? 'C'} />
            ) : (
              <>
                <Link href={siteHref('/sign-in')} className="px-3.5 py-2 text-label1Regular text-lawkitGray-700 rounded-lg hover:bg-lawkitGray-100 hover:text-lawkitGray-950 transition-colors">
                  로그인
                </Link>
                <Link
                  href={siteHref('/sign-up')}
                  className="px-3.5 py-2 text-label1Regular text-lawkitGray-700 rounded-lg hover:bg-lawkitGray-100 hover:text-lawkitGray-950 transition-colors border-l border-lawkitGray-200"
                >
                  회원가입
                </Link>
              </>
            )}
            {isLogin && (
              <Btn to={siteHref('/quick-intake')} variant="primary" size="sm" className="ml-2">
                {INQUIRY_HEADING}
              </Btn>
            )}
            {showInquiryCta && (
              <Btn href={inquiryHref} variant="primary" size="sm" className="ml-2" onClick={onInquiryClick}>
                {INQUIRY_HEADING}
              </Btn>
            )}
          </div>

          {/* Mobile hamburger — visible below lg */}
          <button
            ref={hamburgerRef}
            type="button"
            className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-lawkitGray-100 transition-colors ${drawerOpen ? 'bg-lawkitGray-100' : ''}`}
            aria-label="메뉴"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(o => !o)}
          >
            {/* Animated hamburger lines */}
            <span className="relative w-[18px] h-[14px] flex flex-col justify-between">
              <span className={`block w-full h-[1.5px] bg-lawkitGray-950 rounded-full transition-transform duration-200 origin-center ${drawerOpen ? 'translate-y-[6.25px] rotate-45' : ''}`} />
              <span className={`block w-full h-[1.5px] bg-lawkitGray-950 rounded-full transition-opacity duration-200 ${drawerOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-full h-[1.5px] bg-lawkitGray-950 rounded-full transition-transform duration-200 origin-center ${drawerOpen ? '-translate-y-[6.25px] -rotate-45' : ''}`} />
            </span>
          </button>
        </div>

        {/* Services mega panel */}
        {open === 'services' && servicesSide && (
          <MegaPanel label="서비스" id={megaPanelId('services')} onMouseEnter={() => openMega('services')} onMouseLeave={scheduleClose} onFocus={() => openMega('services')} onBlur={scheduleClose}>
            <div className={`${container} py-7 pb-8 grid grid-cols-[280px_1fr] gap-10`}>
              {/* Side blurb */}
              <div className="border-r border-lawkitGray-200 pr-6">
                <MegaSide side={servicesSide} onCtaClick={() => setOpen(null)} onInquiryFallback={openInquiryModal} inquiryHref={inquiryHref} mainOrigin={mainOrigin} />
              </div>

              {/* Service cards grid */}
              <div className="grid grid-cols-2 gap-2 content-start">
                {SERVICES.map(s => (
                  <Link
                    key={s.slug}
                    href={siteHref(`/services/${s.slug}`)}
                    className="grid grid-cols-[32px_1fr_auto] gap-3.5 items-center px-3.5 py-3.5 rounded-[10px] hover:bg-lawkitGray-50 transition-colors group"
                  >
                    <span className="text-[11px] font-bold tracking-[0.06em] text-lawkitGray-400">{s.n}</span>
                    <div className="min-w-0">
                      <div className="text-body3Bold text-lawkitGray-950 mb-0.5 truncate">{s.title}</div>
                      <div className="text-[13px] text-lawkitGray-500 leading-[1.45] line-clamp-2">{s.tagline}</div>
                    </div>
                    <span className="text-lawkitGray-300 group-hover:text-lawkitBlue-70 group-hover:translate-x-0.5 transition-[color,transform] duration-150">
                      <Icon name={ICON_MAP.arrowRight} className="text-[16px]" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </MegaPanel>
        )}

        {/* mega-compact panels — rendered generically for all matching nav items */}
        {megaCompactItems.map(item =>
          open === item.key ? (
            <MegaPanel
              key={item.key}
              label={item.label}
              id={megaPanelId(item.key)}
              onMouseEnter={() => openMega(item.key)}
              onMouseLeave={scheduleClose}
              onFocus={() => openMega(item.key)}
              onBlur={scheduleClose}
            >
              <div className={`${container} py-7 pb-8 grid grid-cols-[240px_1fr] gap-10`}>
                {/* Side blurb */}
                <div className="border-r border-lawkitGray-200 pr-6">
                  <MegaSide side={item.side} onCtaClick={() => setOpen(null)} onInquiryFallback={openInquiryModal} inquiryHref={inquiryHref} mainOrigin={mainOrigin} />
                </div>

                {/* Items grid — one row per panel, equal-width columns sized to item count
                    (3 for support, 4 for media center — both stay on a single row). */}
                <div className="grid grid-flow-col auto-cols-fr gap-2 content-start">
                  {item.items.map(it => (
                    <BoundaryLink
                      key={it.to}
                      href={siteHref(it.to)}
                      // 패널은 경로가 바뀔 때만 닫힌다(위 useEffect). 요금 메뉴의 세 항목은
                      // 같은 페이지 안의 앵커(`/plan#…`)라 경로가 안 바뀌어서, 이미 /plan 에
                      // 있던 사람이 고르면 스크롤만 내려가고 패널이 목표 구획을 덮은 채 남았다.
                      onClick={() => setOpen(null)}
                      className="grid grid-cols-[1fr_auto] gap-3.5 items-center col-span-1 px-3.5 py-3.5 rounded-[10px] hover:bg-lawkitGray-50 transition-colors group"
                    >
                      <div className="min-w-0">
                        <div className="text-body3Bold text-lawkitGray-950 mb-0.5">{it.label}</div>
                        <div className="text-[13px] text-lawkitGray-500 leading-[1.45]">{it.sub}</div>
                      </div>
                      <span className="text-lawkitGray-300 group-hover:text-lawkitBlue-70 group-hover:translate-x-0.5 transition-[color,transform] duration-150">
                        <Icon name={ICON_MAP.arrowRight} className="text-[16px]" />
                      </span>
                    </BoundaryLink>
                  ))}
                </div>
              </div>
            </MegaPanel>
          ) : null,
        )}
      </header>

      {/* Mobile drawer — rendered OUTSIDE <header> on purpose. The header has
          backdrop-filter (backdrop-blur), which makes it the containing block
          for position:fixed descendants; keeping the drawer inside would trap
          its fixed panel/backdrop within the 84px header box. */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        triggerRef={hamburgerRef}
        topOffset={drawerTop}
        isLogin={isLogin}
        userRole={userRole}
        onSwitchLocale={switchToEnglish}
        appMode={appMode}
        hideLocaleSwitch={hideLocaleSwitch}
        inquiryHref={inquiryHref}
        mainOrigin={mainOrigin}
        onInquiryFallback={() => {
          setDrawerOpen(false);
          openInquiryModal();
        }}
      />

      {/* 서랍은 닫히면서 스스로 언마운트된다 — 모달은 그 밖에 있어야 살아남는다. */}
      {inquiryOpen && (
        <InquiryModal
          heading={INQUIRY_HEADING}
          notice={INQUIRY_NOTICE}
          onClose={() => {
            setInquiryOpen(false);
            rescueFocusAfterClose();
          }}
        />
      )}
    </>
  );
}
